import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useAITutorStore } from "@/stores/ai-tutor-store";
import { cn } from "@/lib/cn";

const MIN_SELECTION_LENGTH = 3;
const SELECTION_DELAY_MS = 160;
const MAX_REFERENCE_LENGTH = 600;

type Coords = { top: number; left: number; mobile: boolean };

/**
 * Floating "Ask AI" popup that appears above any user text selection
 * inside the current view. Mirrors v1's chat-widget.js text selection
 * popup but with native pointer events (works for both mouse and touch
 * without dual-binding), brand-tinted styling, and zero impact on text
 * selection performance (debounced 160ms so the user can finish their
 * drag before the chrome appears).
 *
 * The popup never appears on top of:
 *   - selections inside form inputs
 *   - selections inside the AI Tutor sheet itself (recursive)
 *   - empty / single-character selections
 */
export function TextSelectionPopup() {
  const open = useAITutorStore((s) => s.open);
  const isSheetOpen = useAITutorStore((s) => s.isOpen);

  const [coords, setCoords] = useState<Coords | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const hide = useCallback(() => {
    setCoords(null);
    setReference(null);
  }, []);

  const trySelection = useCallback(() => {
    if (isSheetOpen) return;
    const selection = window.getSelection();
    const text = selection?.toString().trim() ?? "";
    if (!selection || text.length < MIN_SELECTION_LENGTH || !selection.rangeCount) {
      hide();
      return;
    }
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    // If selection is inside an input / textarea / the sheet itself, skip.
    const containerEl = container.nodeType === Node.ELEMENT_NODE
      ? (container as HTMLElement)
      : container.parentElement;
    if (!containerEl) { hide(); return; }
    if (containerEl.closest("input, textarea, [data-ai-tutor-sheet]")) {
      hide();
      return;
    }

    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) { hide(); return; }

    setReference(text.slice(0, MAX_REFERENCE_LENGTH));
    const mobile = window.matchMedia("(max-width: 639px), (pointer: coarse)").matches;
    if (mobile) {
      // Selection handles and the browser's own copy menu move around as the
      // user adjusts a mobile selection. A stable, thumb-reachable action is
      // more reliable than trying to chase the Range rectangle.
      setCoords({ top: 0, left: 0, mobile: true });
      return;
    }
    // Position the popup centred above the selection, clamped to the viewport.
    requestAnimationFrame(() => {
      const popup = popupRef.current;
      const popupW = popup?.offsetWidth ?? 180;
      const popupH = popup?.offsetHeight ?? 36;
      const margin = 8;
      let top = rect.top - popupH - margin;
      if (top < margin) top = rect.bottom + margin;
      let left = rect.left + (rect.width - popupW) / 2;
      if (left < margin) left = margin;
      const maxLeft = window.innerWidth - popupW - margin;
      if (left > maxLeft) left = Math.max(margin, maxLeft);
      setCoords({ top, left, mobile: false });
    });
  }, [hide, isSheetOpen]);

  // Subscribe to selection-change once and debounce. selectionchange is
  // more reliable than mouseup+touchend on iOS Safari, where touch
  // selection completes with synthesized selectionchange events.
  useEffect(() => {
    const handler = () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(trySelection, SELECTION_DELAY_MS);
    };
    document.addEventListener("selectionchange", handler);
    // Touch-end as a backup: some Android keyboards don't fire
    // selectionchange consistently after the long-press menu dismisses.
    document.addEventListener("touchend", handler, { passive: true });
    return () => {
      document.removeEventListener("selectionchange", handler);
      document.removeEventListener("touchend", handler);
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [trySelection]);

  // Hide on outside click / scroll while visible.
  useEffect(() => {
    if (!coords) return;
    const onScroll = () => hide();
    const onClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) hide();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("mousedown", onClickOutside);
    };
  }, [coords, hide]);

  const handleAsk = () => {
    if (!reference) return;
    open({ reference });
    // Clear the highlight so the popup doesn't reappear when the sheet closes.
    window.getSelection()?.removeAllRanges();
    hide();
  };

  return (
    <AnimatePresence>
      {coords && reference ? (
        <motion.div
          ref={popupRef}
          key="selection-popup"
          initial={{ opacity: 0, y: 4, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.96 }}
          transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
          className={cn(
            "fixed z-[55] pointer-events-auto",
            coords.mobile && "inset-x-4 bottom-[calc(5rem+env(safe-area-inset-bottom))]",
          )}
          style={coords.mobile ? undefined : { top: coords.top, left: coords.left }}
        >
          <button
            type="button"
            onClick={handleAsk}
            // Act on touchstart so iOS cannot clear the Range between the tap
            // and its delayed synthetic click. Mouse/trackpad keeps onClick.
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAsk();
            }}
            className={cn(
              "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-brand text-white text-xs font-medium shadow-lg hover:brightness-110 active:scale-[0.97] transition-all",
              coords.mobile && "w-full min-h-12 text-sm",
            )}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Ask AI about this
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
